import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DateInput,
  Dropdown,
  InputField,
  SubmitButton,
} from "../../../../components";

import styles from './InstructionSetup.module.css';
import HeadingComponent from '../../../../components/HeadingComponent/HeadingComponent';

const MACHINE_OPTIONS = [
  { label: 'Mixing', value: 'Mixing' },
  { label: 'Baking', value: 'Baking' },
  { label: 'Cooling', value: 'Cooling' },
];

const createEmptyStage = () => ({
  id: crypto.randomUUID(),
  stageName: '',
  stageTime: '',
  machine: '',
  parameters: { time: '', temperature: '' },
  additionalParameters: [
    { id: crypto.randomUUID(), key: '', value: '' },
  ],
  instructions: [{ id: crypto.randomUUID(), text: '' }],
});

function SortableStageTab({
  stage,
  index,
  active,
  onSelect,
  onDelete,
  disableDelete,
}) {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id: stage.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`${styles.tabWrapper} ${active ? styles.activeTab : ''
        }`}
    >
      <button
        onClick={onSelect}
        className={styles.tabButton}
        type="button"
      >
        Stage {index + 1}
      </button>

      <span
        className={styles.drag}
        {...attributes}
        {...listeners}
      >
        ☰
      </span>

      <button
        className={styles.deleteStage}
        onClick={onDelete}
        disabled={disableDelete}
        type="button"
      >
        ✖
      </button>
    </div>
  );
}

function SortableInstruction({ item, onChange, onRemove }) {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={styles.instructionRow}
    >
      <InputField
        label="Instruction *"
        value={item.text}
        placeholder="Enter instruction"
        onChange={(e) => onChange(e.target.value)}
      />

      <button
        className={styles.deleteBtn}
        onClick={onRemove}
        type="button"
      >
        ✖
      </button>

      <span
        className={styles.drag}
        {...attributes}
        {...listeners}
      >
        ☰
      </span>
    </div>
  );
}


export default function InstructionSetup() {
  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(0);
  const [errors, setErrors] = useState({});

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    setStages([createEmptyStage()]);
  }, []);

  if (!stages.length) return null;

  const stage = stages[activeStage];

  if (!Array.isArray(stage.additionalParameters)) {
    stage.additionalParameters = [];
  }


  const updateStageValue = (key, value) => {
    const copy = [...stages];
    copy[activeStage][key] = value;
    setStages(copy);
  };

  const validate = () => {
    const e = {};

    if (!stage.stageName?.trim())
      e.stageName = 'Stage Name is required';

    if (!stage.stageTime?.trim())
      e.stageTime = 'Stage Time is required';

    if (!stage.instructions.length ||
      stage.instructions.some((i) => !i.text.trim()))
      e.instructions = 'At least one instruction is required';

    if (stage.machine) {
      if (!stage.parameters?.time?.trim())
        e.methodTime = 'Time is required';

      if (!stage.parameters?.temperature?.trim())
        e.methodTemperature = 'Temperature is required';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };



  const saveProcess = async () => {
    if (!validate()) return;

    const formattedStages = stages.map((s) => {
      const hasMachine = !!s.machine;

      return {
        stageName: s.stageName,
        stageTime: s.stageTime,

        method: hasMachine ? {
          machine: s.machine,
          parameters: {
            time: s.parameters?.time || null,
            temperature: s.parameters?.temperature || null,
          },
        } : {},

        additionalParameters:
          s.additionalParameters?.filter(
            (p) => p.key || p.value
          ) || [],
        instructions: s.instructions
          .map((i) => i.text.trim())
          .filter((text) => text !== ""),
      };
    });

    console.log("Saved Data", formattedStages);
  };



  const deleteStage = (index) => {
    if (stages.length === 1) return;
    if (!window.confirm(`Delete Stage ${index + 1}?`))
      return;

    const updated = stages.filter((_, i) => i !== index);
    setStages(updated);
    setActiveStage(Math.max(0, index - 1));
  };

  const onStageDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = stages.findIndex(
      (s) => s.id === active.id
    );
    const newIndex = stages.findIndex(
      (s) => s.id === over.id
    );

    setStages(arrayMove(stages, oldIndex, newIndex));
    setActiveStage(newIndex);
  };

  const onInstructionDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = stage.instructions.findIndex(
      (i) => i.id === active.id
    );
    const newIndex = stage.instructions.findIndex(
      (i) => i.id === over.id
    );

    const copy = [...stages];
    copy[activeStage].instructions = arrayMove(
      stage.instructions,
      oldIndex,
      newIndex
    );
    setStages(copy);
  };

  const addParameter = () => {
    const copy = [...stages];
    copy[activeStage].additionalParameters.push({
      id: crypto.randomUUID(),
      key: '',
      value: '',
    });
    setStages(copy);
  };

  const updateParameter = (i, field, value) => {
    const copy = [...stages];
    copy[activeStage].additionalParameters[i][field] =
      value;
    setStages(copy);
  };

  const removeParameter = (i) => {
    const copy = [...stages];
    copy[activeStage].additionalParameters.splice(i, 1);
    setStages(copy);
  };


  return (
    <div className={styles.wrapper}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onStageDragEnd}
      >
        <SortableContext
          items={stages.map((s) => s.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className={styles.tabs}>
            {stages.map((s, i) => (
              <SortableStageTab
                key={s.id}
                stage={s}
                index={i}
                active={i === activeStage}
                onSelect={() => setActiveStage(i)}
                onDelete={() => deleteStage(i)}
                disableDelete={stages.length === 1}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className={styles.actions}>
        <SubmitButton
          title="Add A Stage"
          primary
          onClick={() => {
            setStages([...stages, createEmptyStage()]);
            setActiveStage(stages.length);
          }}
        />
        <SubmitButton
          title="Save Process"
          primary
          onClick={saveProcess}
        />
      </div>

      <section className={styles.card}>
        <HeadingComponent text={"Basic Information *"} fontSize={16} />
        <div className={styles.grid2}>
          <InputField
            label="Stage Name *"
            value={stage.stageName}
            onChange={(e) =>
              updateStageValue(
                'stageName',
                e.target.value
              )
            }
            error={errors.stageName}
          />
          <InputField
            label="Stage Time *"
            value={stage.stageTime}
            onChange={(e) =>
              updateStageValue(
                'stageTime',
                e.target.value
              )
            }
            error={errors.stageTime}
          />
        </div>
      </section>

      <section className={`${styles.card} ${styles.methodCard}`}>
        <HeadingComponent text={"Method"} fontSize={16} />

        <Dropdown
          label="Machine Used *"
          placeholder="Select Machine"
          value={stage.machine}
          options={MACHINE_OPTIONS}
          onChange={(val) =>
            updateStageValue('machine', val)
          }
        />

        <div className={styles.methodParams}>
          <InputField
            label="Time"
            value={stage.parameters.time}
            placeholder="Minutes"
            error={errors.methodTime}
            onChange={(e) => {
              const copy = [...stages];
              copy[activeStage].parameters.time =
                e.target.value;
              setStages(copy);
            }}
          />
          <InputField
            label="Temperature"
            value={stage.parameters.temperature}
            placeholder="°C"
            onChange={(e) => {
              const copy = [...stages];
              copy[activeStage].parameters.temperature =
                e.target.value;
              setStages(copy);
            }}
            error={errors.methodTemperature}
          />
        </div>
      </section>

      <section className={styles.card}>
        <HeadingComponent text={"Additional Parameters"} fontSize={16} />

        {stage.additionalParameters.map((p, i) => (
          <div key={p.id} className={styles.paramRow}>
            <InputField
              label="Field Name"
              value={p.key}
              onChange={(e) =>
                updateParameter(i, 'key', e.target.value)
              }
            />
            <InputField
              label="Field Value"
              value={p.value}
              onChange={(e) =>
                updateParameter(
                  i,
                  'value',
                  e.target.value
                )
              }
            />
            <button
              className={styles.deleteBtn}
              onClick={() => removeParameter(i)}
              type="button"
            >
              ✖
            </button>
          </div>
        ))}

        <SubmitButton
          title="Add Parameter"
          onClick={addParameter}
        />
      </section>

      <section className={styles.card}>
        <HeadingComponent text={"Instructions *"} fontSize={16} />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onInstructionDragEnd}
        >
          <SortableContext
            items={stage.instructions.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {stage.instructions.map((inst, i) => (
              <SortableInstruction
                key={inst.id}
                item={inst}
                onChange={(val) => {
                  const copy = [...stages];
                  copy[activeStage].instructions[i].text = val;
                  setStages(copy);
                }}
                onRemove={() => {
                  const copy = [...stages];
                  copy[activeStage].instructions.splice(i, 1);
                  setStages(copy);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>

        {errors.instructions && (
          <span className={styles.error}>
            {errors.instructions}
          </span>
        )}

        <SubmitButton
          title="Add Instruction"
          onClick={() => {
            const copy = [...stages];
            copy[activeStage].instructions.push({
              id: crypto.randomUUID(),
              text: '',
            });
            setStages(copy);
          }}
        />
      </section>
    </div>
  );
}
